import { Paper, Popper } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createEmptyConditionItem } from "../utilities/createEmptyConditionItem";
import type { FormulaInputActions, FormulaInputState } from "./hooks/formulaInputState";
import type { CustomMeasureReturnType } from "./hooks/useCustomMeasure";
import IntellisenseItem from "./IntellisenseItem";
import { setCaretIndex } from "./utilities/caretPosition";
import { findNode } from "./utilities/fillNodes";
import { hasSpaceCharacter } from "./utilities/tokenizer";
import { DimensionFormulaVariable, FunctionFormulaVariable } from "./utilities/variables";
import { getFunctionVariables } from "./utilities/variables.utils";

interface Props {
  state: CustomMeasureReturnType;
  formulaState: FormulaInputState;
  actions: FormulaInputActions;
}

export const Intellisense = ({ state, formulaState, actions }: Props) => {
  const [searchText, setSearchText] = useState<string>();
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [showPopup, setShowPopup] = useState(false);

  const tokenRef = useRef(state.currentToken);
  const selectedIndexRef = useRef(selectedIndex);
  useEffect(() => {
    tokenRef.current = state.currentToken;
  }, [state.currentToken]);
  useEffect(() => {
    selectedIndexRef.current = selectedIndex;
  }, [selectedIndex]);

  useEffect(() => {
    if (
      typeof state.currentToken?.value === "string" &&
      !!state.currentToken.text &&
      isTextLengthValid(state.currentToken.value.length)
    ) {
      // TODO #209
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setSearchText(state.currentToken.text);
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setShowPopup(true);
    } else {
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setSearchText(undefined);
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setShowPopup(false);
    }
  }, [state.currentToken, setSearchText]);

  useEffect(() => {
    // TODO #209
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setSelectedIndex(0);
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setShowPopup(true);
  }, [searchText]);

  const variables = useMemo(() => {
    // Dimensions are temporary disabled
    // return getDimensionVariables(state.dimensions).concat(getFunctionVariables(state.functions));
    return getFunctionVariables(state.functions);
  }, [state.functions]);

  const suggestions = useCallback(
    (searchText?: string) => {
      if (searchText === undefined || searchText === null) return [];
      return variables.filter((v) => v.caption.toLowerCase().includes(searchText.toLowerCase()));
    },
    [variables]
  );

  const currentSuggestions = suggestions(searchText);
  const suggestionsRef = useRef(currentSuggestions);
  useEffect(() => {
    suggestionsRef.current = currentSuggestions;
  }, [currentSuggestions]);

  useEffect(() => {
    if (selectedIndexRef.current > 0) {
      setSelectedIndex(selectedIndexRef.current - 1);
    }
  }, [formulaState.previous]);

  useEffect(() => {
    if (selectedIndexRef.current < suggestionsRef.current.length - 1) {
      setSelectedIndex(selectedIndexRef.current + 1);
    }
  }, [formulaState.next]);

  useEffect(() => {
    const suggest = suggestionsRef.current[selectedIndexRef.current];
    if (suggest === undefined) return;
    if (tokenRef.current?.element) {
      const relatedNode = findNode(state.formulaNodes, tokenRef.current);
      if (relatedNode !== undefined) {
        if (suggest instanceof DimensionFormulaVariable) {
          state.actions.updateFormulaNode(relatedNode, {
            conditions: [createEmptyConditionItem(structuredClone(suggest.field))],
          });
        } else if (suggest instanceof FunctionFormulaVariable) {
          state.actions.updateFormulaNode(relatedNode, { function: suggest.field });
        }
      }
      tokenRef.current.element.textContent = hasSpaceCharacter(suggest.caption)
        ? `"${suggest.caption}"`
        : suggest.caption;
      setCaretIndex(tokenRef.current.element, tokenRef.current.element.innerText.length);
      actions.inserted();
      setShowPopup(false);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [formulaState.select]);

  useEffect(() => {
    // TODO #209
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setShowPopup(false);
  }, [formulaState.close]);

  if (!state.currentToken?.element) {
    return <></>;
  }
  if (!document.body.contains(state.currentToken.element)) {
    return <></>;
  }
  if (
    state.currentToken.value &&
    typeof state.currentToken.value === "string" &&
    !isTextLengthValid(state.currentToken.value.length)
  ) {
    return <></>;
  }
  if (currentSuggestions.length === 0) {
    return <></>;
  }

  return (
    <Popper
      open={showPopup}
      anchorEl={state.currentToken.element}
      placement="bottom-start"
      sx={{ bgcolor: "background.paper", zIndex: 1400 }}
    >
      <Paper sx={{ ml: -4.5, mt: 0.5 }}>
        {currentSuggestions.map((s, index) => (
          <IntellisenseItem
            key={s.key}
            searchText={searchText || ""}
            suggestion={s}
            selected={selectedIndex === index}
            onSelect={() => {
              setSelectedIndex(index);
              actions.select();
            }}
          />
        ))}
      </Paper>
    </Popper>
  );
};

export default Intellisense;

const isTextLengthValid = (length: number) => {
  return !(length < 2);
};
